import * as yup from 'yup';
import has from 'lodash/has';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isPast from 'date-fns/isPast';
import isToday from 'date-fns/isToday';
import isBefore from 'date-fns/isBefore';
import { isDate } from 'lodash';
import { isMoment } from 'moment';
import { AUSTRALIA } from 'utils/constants';

export const textFieldRegex = /^[a-zA-Z0-9]+$/;
export const textFieldRegexWithSpace = /^[a-zA-Z0-9 ]+$/;
export const textAreaRegex =
  /^$|^(\s|\w|<br>|,|{|}|\?|;|:|\+|@|=|!|`|~|\^|\$|'|%|\(|\)|-|\||\.|\/|#|\*|–|&|%|€“|\[|\]|\\)*?$/;
const postCodeErrorMessage = 'Invalid postcode';

const postCodeSchema = (prefix: number) =>
  yup
    .string()
    .required()
    .matches(new RegExp(`^${prefix}[0-9]{3}$`), postCodeErrorMessage);

const nonAuPostCodeSchema = yup
  .string()
  .required()
  .max(15, postCodeErrorMessage)
  .trim()
  .matches(textAreaRegex, postCodeErrorMessage);

const statePostCodePrefix = {
  ACT: 2,
  NSW: 2,
  VIC: 3,
  QLD: 4,
  SA: 5,
  WA: 6,
  TAS: 7,
  NT: 0,
};

const statePostCodePrefixValues = Object.values(statePostCodePrefix);
type State = 'ACT' | 'NSW' | 'QLD';
type PostCodeMap = Record<State, string[]>;

const otherFixedPostcode: PostCodeMap = {
  ACT: ['2620'],
  NSW: ['4375', '4380', '4385'],
  QLD: ['2460'],
};

export const validatePostCode = (country: string) => (state: State) => (postCode: string) => {
  const isAu = country === AUSTRALIA;

  if (isAu) {
    if (has(otherFixedPostcode, state) && get(otherFixedPostcode, state).includes(postCode)) {
      return true;
    }

    if (isEmpty(state) && !isEmpty(postCode)) {
      return statePostCodePrefixValues.some((prefix) => postCodeSchema(prefix).isValidSync(postCode));
    }

    if (has(statePostCodePrefix, state)) {
      return postCodeSchema(get(statePostCodePrefix, state)).isValidSync(postCode);
    }
    return false;
  }
  return nonAuPostCodeSchema.isValidSync(postCode);
};

interface DecimalParams {
  errorMessage: string;
  required?: boolean;
}

export const validateDecimalSchema = ({ errorMessage, required = false }: DecimalParams) => {
  if (!required) {
    return yup
      .number()
      .min(0, errorMessage)
      .transform((_, value) => (value === '' ? null : +value.replace(/[\,]/g, '')))
      .nullable()
      .typeError(errorMessage);
  }
  return yup
    .number()
    .typeError(errorMessage)
    .min(1, errorMessage)
    .transform((_, v) => (v === '' ? null : +v.replace(/[\,]/g, '')))
    .required(errorMessage)
    .moreThan(-1, errorMessage)
    .required(errorMessage);
};

interface DateParams {
  hasMinDate?: boolean;
}

export const validateDateSchema = ({ hasMinDate = false }: DateParams) => {
  if (!hasMinDate) {
    return yup
      .date()
      .required('Please enter a valid date')
      .typeError('Please enter a valid date')
      .test('date-check', 'Please enter a valid date', (value) => {
        const date = isMoment(value) ? value.toDate() : value;
        return isDate(date) && (isPast(date) || isToday(date));
      });
  }

  return yup
    .date()
    .required('Please enter a valid date of birth')
    .typeError('Please enter a valid date of birth')
    .test('dob-check', 'Please enter a valid date of birth', (value) => {
      const date = isMoment(value) ? value.toDate() : value;
      return isDate(date) && (isPast(date) || isToday(date));
    })
    .test('dob-check', 'DOB cannot be before 1/1/1900', (value) => {
      const date = isMoment(value) ? value.toDate() : value;
      return isDate(date) && !isBefore(date, new Date(1900, 0, 1));
    });
};

interface TextProps {
  minLen: number;
  maxLen: number;
  required?: boolean;
}

export const validateTextSchema = ({ minLen, maxLen, required = false }: TextProps) => {
  const defaultErrorMsg = 'Please enter a valid value';

  const specialErrorMsg =
    "For security reasons, only the following special characters are allowed: <br> , { } ? ; : + @ = ! ` ~ ^ $ ' % ( ) - . / # * – & % €“ [ ] \\ ";

  if (!required)
    return yup
      .string()
      .min(minLen, defaultErrorMsg)
      .max(maxLen, defaultErrorMsg)
      .trim()
      .matches(textAreaRegex, specialErrorMsg);

  return yup
    .string()
    .min(minLen, defaultErrorMsg)
    .max(maxLen, defaultErrorMsg)
    .trim()
    .matches(textAreaRegex, specialErrorMsg)
    .required(defaultErrorMsg);
};
